// =====================================================================================
// INVRT Radar - web console - the operator organisations admin client (Story PS5)
//
// The browser client for the PS2 seam (routes/organisations_admin.ts, contracts C1 and
// C2): POST /v1/admin/organisations and POST /v1/admin/organisations/:org_id/invites.
//
// The types.radar.ts law holds: the types and parsers below describe exactly what the two
// routes return and nothing more; a response that is structurally nonsense fails closed as
// an ApiError instead of rendering half a page. The C1 parser pins the FULL audited
// admin_create_organization row (the RPC returns to_jsonb(organizations.*)), so a response
// carrying any key outside the audited row shape, or an invite response that leaks a
// privileged credential, is refused here rather than rendered.
// =====================================================================================

import { ApiError } from "@/lib/api";
import type { useApiClient } from "@/hooks/useApi";

type ApiClient = ReturnType<typeof useApiClient>;

export type OrganisationKind = "tenant" | "reseller";
export type MembershipRole = "member" | "admin";

/** The organisations row exactly as the audited RPC returns it (migration 0050). */
export interface OrganisationRecord {
  id: string;
  name: string;
  slug: string;
  tenant_class: "internal" | "external";
  region: string | null;
  status: "pending" | "active" | "suspended" | "closed";
  daily_cap_credits: number | null;
  created_at: string;
  updated_at: string;
  parent_org_id: string | null;
  org_kind: OrganisationKind;
  billing_suspended: boolean;
}

export interface CreateOrganisationInput {
  name: string;
  org_kind: OrganisationKind;
  /** Null for a top-level organisation; otherwise an existing reseller's id. */
  parent_org_id: string | null;
  reason: string;
}

export interface CreateOrganisationResponse {
  organisation: OrganisationRecord;
}

export interface InviteMemberInput {
  email: string;
  membership_role: MembershipRole;
  module_codes: string[];
  campaign_ids: string[];
  reason: string;
}

/** The whitelisted 202 body: built field by field on the server, no credential carries. */
export interface InviteMemberResponse {
  organisation_id: string;
  user_id: string;
  email: string;
  is_org_admin: boolean;
  invited: boolean;
  module_grants: Array<{ module_code: string; id: string | null }>;
  module_subscriptions: Array<{ module_id: string; id: string | null }>;
  campaign_subscriptions: Array<{ campaign_id: string; id: string | null }>;
  /** Optional sibling: present only when campaigns were requested (post-commit anchor verdicts). */
  campaign_anchors?: InviteCampaignAnchor[];
}

/** The post-commit HIGH-INTENT anchor verdict for one invited campaign. */
export type InviteAnchorVerdict =
  | { anchored: false; reason?: string }
  | { anchored: true; high_intent_org_id: string; high_intent_campaign_key: string; inserted: boolean };

export interface InviteCampaignAnchor {
  campaign_id: string;
  anchor: InviteAnchorVerdict | null;
}

// ---- fail-closed parsing primitives (the house idiom) ----------------------------------

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const BAD_CONTRACT = "The organisation admin response did not match the reviewed contract.";

function invalidContract(): ApiError {
  return new ApiError({
    token: "internal_error",
    message: BAD_CONTRACT,
    httpStatus: 502,
    correlationId: null,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const received = Object.keys(value).sort();
  const expected = [...keys].sort();
  return received.length === expected.length && received.every((key, index) => key === expected[index]);
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isDateTime(value: unknown): value is string {
  if (typeof value !== "string" || !DATE_TIME_PATTERN.test(value)) return false;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:\d{2})$/);
  if (!match) return false;
  const year = Number(match[1]!);
  const month = Number(match[2]!);
  const day = Number(match[3]!);
  const hour = match[4]!;
  const minute = match[5]!;
  const second = match[6]!;
  const offset = match[7]!;
  if (month < 1 || month > 12 || hour > "23" || minute > "59" || second > "59") return false;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day < 1 || day > daysInMonth) return false;
  if (offset === "Z") return true;
  const offsetHours = Number(offset.slice(1, 3));
  const offsetMinutes = Number(offset.slice(4, 6));
  return offsetHours <= 23 && offsetMinutes <= 59;
}

/** A jsonb numeric arrives as a finite JSON number, or is absent (SQL NULL). */
function isCreditCap(value: unknown): value is number | null {
  return value === null || (typeof value === "number" && Number.isFinite(value) && value >= 0);
}

const ORGANISATION_KEYS = [
  "id",
  "name",
  "slug",
  "tenant_class",
  "region",
  "status",
  "daily_cap_credits",
  "created_at",
  "updated_at",
  "parent_org_id",
  "org_kind",
  "billing_suspended",
] as const;

const ORGANISATION_STATUSES = ["pending", "active", "suspended", "closed"] as const;
const TENANT_CLASSES = ["internal", "external"] as const;
export const ORGANISATION_KINDS: readonly OrganisationKind[] = ["tenant", "reseller"];

function isOrganisationRecord(value: unknown): value is OrganisationRecord {
  if (!isRecord(value) || !hasExactKeys(value, ORGANISATION_KEYS)) return false;
  if (!isUuid(value.id)) return false;
  if (typeof value.name !== "string" || value.name.length < 1 || value.name.length > 120) return false;
  if (typeof value.slug !== "string" || value.slug.length < 1 || value.slug.length > 120) return false;
  if (!TENANT_CLASSES.includes(value.tenant_class as OrganisationRecord["tenant_class"])) return false;
  if (!ORGANISATION_STATUSES.includes(value.status as OrganisationRecord["status"])) return false;
  if (!isCreditCap(value.daily_cap_credits)) return false;
  if (!isDateTime(value.created_at) || !isDateTime(value.updated_at)) return false;
  if (value.parent_org_id !== null && !isUuid(value.parent_org_id)) return false;
  if (!ORGANISATION_KINDS.includes(value.org_kind as OrganisationKind)) return false;
  if (typeof value.billing_suspended !== "boolean") return false;
  return true;
}

export function parseCreatedOrganisation(value: unknown): CreateOrganisationResponse {
  if (!isRecord(value) || Object.keys(value).length !== 1 || !("organisation" in value)) {
    throw invalidContract();
  }
  if (!isOrganisationRecord(value.organisation)) throw invalidContract();
  return { organisation: value.organisation };
}

const INVITE_KEYS = [
  "organisation_id",
  "user_id",
  "email",
  "is_org_admin",
  "invited",
  "module_grants",
  "module_subscriptions",
  "campaign_subscriptions",
] as const;

function isGrantEntry(value: unknown): value is { module_code: string; id: string | null } {
  if (!isRecord(value) || !hasExactKeys(value, ["module_code", "id"])) return false;
  return typeof value.module_code === "string" && value.module_code.length >= 1 && value.module_code.length <= 64 && (value.id === null || isUuid(value.id));
}

function isModuleSubscriptionEntry(value: unknown): value is { module_id: string; id: string | null } {
  if (!isRecord(value) || !hasExactKeys(value, ["module_id", "id"])) return false;
  return isUuid(value.module_id) && (value.id === null || isUuid(value.id));
}

function isCampaignSubscriptionEntry(value: unknown): value is { campaign_id: string; id: string | null } {
  if (!isRecord(value) || !hasExactKeys(value, ["campaign_id", "id"])) return false;
  return isUuid(value.campaign_id) && (value.id === null || isUuid(value.id));
}

/**
 * The closed set of reason tokens the server can emit on an unanchored invite verdict
 * (organisations_admin.ts via ANCHOR_REASON in access.ts). Anything else is a contract
 * drift and is refused, fail closed.
 */
export const ANCHOR_REASONS: ReadonlySet<string> = new Set([
  "anchor_refused_by_high_intent",
  "anchor_provider_timeout",
  "anchor_skipped_budget_exhausted",
  "anchor_skipped_provider_unavailable",
  "anchor_skipped_provider_timeout",
]);

function parseAnchorVerdict(value: unknown): InviteAnchorVerdict | null | undefined {
  if (value === null) return null;
  if (!isRecord(value)) return undefined;
  if (hasExactKeys(value, ["anchored"]) && value.anchored === false) return { anchored: false };
  if (
    hasExactKeys(value, ["anchored", "reason"]) && value.anchored === false &&
    typeof value.reason === "string" && ANCHOR_REASONS.has(value.reason)
  ) {
    return { anchored: false, reason: value.reason };
  }
  if (
    hasExactKeys(value, ["anchored", "high_intent_org_id", "high_intent_campaign_key", "inserted"]) &&
    value.anchored === true &&
    typeof value.high_intent_org_id === "string" && value.high_intent_org_id.length > 0 &&
    typeof value.high_intent_campaign_key === "string" && value.high_intent_campaign_key.length > 0 &&
    typeof value.inserted === "boolean"
  ) {
    return {
      anchored: true,
      high_intent_org_id: value.high_intent_org_id,
      high_intent_campaign_key: value.high_intent_campaign_key,
      inserted: value.inserted,
    };
  }
  return undefined;
}

function parseCampaignAnchors(value: unknown): InviteCampaignAnchor[] {
  if (!Array.isArray(value)) throw invalidContract();
  return value.map((entry) => {
    if (!isRecord(entry) || !hasExactKeys(entry, ["campaign_id", "anchor"]) || !isUuid(entry.campaign_id)) {
      throw invalidContract();
    }
    const anchor = parseAnchorVerdict(entry.anchor);
    if (anchor === undefined) throw invalidContract();
    return { campaign_id: entry.campaign_id, anchor };
  });
}

export function parseInviteResponse(value: unknown): InviteMemberResponse {
  const hasAnchors = isRecord(value) && "campaign_anchors" in value;
  const { campaign_anchors: rawAnchors, ...base } = isRecord(value) ? value : { campaign_anchors: undefined };
  if (!isRecord(value) || !hasExactKeys(hasAnchors ? base : value, INVITE_KEYS)) throw invalidContract();
  if (!isUuid(value.organisation_id) || !isUuid(value.user_id)) throw invalidContract();
  if (typeof value.email !== "string" || value.email.length < 3 || value.email.length > 320) {
    throw invalidContract();
  }
  if (typeof value.is_org_admin !== "boolean" || typeof value.invited !== "boolean") {
    throw invalidContract();
  }
  if (
    !Array.isArray(value.module_grants) || !value.module_grants.every(isGrantEntry) ||
    !Array.isArray(value.module_subscriptions) || !value.module_subscriptions.every(isModuleSubscriptionEntry) ||
    !Array.isArray(value.campaign_subscriptions) || !value.campaign_subscriptions.every(isCampaignSubscriptionEntry)
  ) {
    throw invalidContract();
  }
  return {
    organisation_id: value.organisation_id,
    user_id: value.user_id,
    email: value.email,
    is_org_admin: value.is_org_admin,
    invited: value.invited,
    module_grants: value.module_grants,
    module_subscriptions: value.module_subscriptions,
    campaign_subscriptions: value.campaign_subscriptions,
    ...(hasAnchors ? { campaign_anchors: parseCampaignAnchors(rawAnchors) } : {}),
  };
}

// ---- the two seam calls -----------------------------------------------------------------

/** C1: create an organisation. The server audits; this client parses fail-closed. */
export async function createOrganisation(
  api: ApiClient,
  input: CreateOrganisationInput,
): Promise<CreateOrganisationResponse> {
  const body = {
    name: input.name,
    org_kind: input.org_kind,
    ...(input.parent_org_id === null ? {} : { parent_org_id: input.parent_org_id }),
    reason: input.reason,
  };
  const response = await api.post<unknown>("/admin/organisations", body, {
    requiresOrg: false,
    includeOrg: false,
  });
  return parseCreatedOrganisation(response);
}

/** C2: invite an email into one organisation, with its role, modules and campaigns. */
export async function inviteOrganisationMember(
  api: ApiClient,
  orgId: string,
  input: InviteMemberInput,
): Promise<InviteMemberResponse> {
  const body = {
    email: input.email,
    membership_role: input.membership_role,
    ...(input.module_codes.length === 0 ? {} : { module_codes: input.module_codes }),
    ...(input.campaign_ids.length === 0 ? {} : { campaign_ids: input.campaign_ids }),
    reason: input.reason,
  };
  const response = await api.post<unknown>(
    `/admin/organisations/${encodeURIComponent(orgId)}/invites`,
    body,
    { requiresOrg: false, includeOrg: false },
  );
  return parseInviteResponse(response);
}
